import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { clampIsoDateToToday, getTodayIsoDate } from "../../../utils/reportDateLimits";
import Backdrop from "@mui/material/Backdrop";
import AppLoader from '../../../components/AppLoader/AppLoader';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import XLSX from "utils/xlsxStyled";
import eOpsLogo from '../../../images/eops.png';
import ReportExportButtons from "../../Report/components/ReportExportButtons";
import "../../Report/reportPageLayout.scss";
import "../../Report/ChangeOfAttributesStatusReport.scss";
import "../../Report/PerformanceEvaluationReport.scss";
import "./CADLineComplaintsStats.scss";
import FESCOLOGO from "../../../images/DISCO Logos/FESCO.png";
import GEPCOLOGO from "../../../images/DISCO Logos/GEPCO.png";
import HESCOLOGO from "../../../images/DISCO Logos/HESCO.png";
import IESCOLOGO from "../../../images/DISCO Logos/IESCO.png";
import MEPCOLOGO from "../../../images/DISCO Logos/MEPCO.png";
import LESCOLOGO from "../../../images/DISCO Logos/LESCO.jpg";
import PESCOLOGO from "../../../images/DISCO Logos/PESCO.png";
import QESCOLOGO from "../../../images/DISCO Logos/QESCO.png";
import SEPCOLOGO from "../../../images/DISCO Logos/SEPCO.png";
import TESCOLOGO from "../../../images/DISCO Logos/TESCO.png";
import { cfpBaseUrl } from '../../../network/urls';
import { openJsPdfInNewTab, convertDate, convertDatewithTime, getName, companyShortName } from '../../../services/Functions';
import '../../../style/common.scss';
import { resolveOfficeCodeForAdmin } from "../../../utils/adminCompany";
import useTechnicalReportLevel from "../../../hooks/useTechnicalReportLevel";
import {
  applyScopedHierarchyFilterLists,
  buildUniqueCircles,
  buildUniqueDivisions,
  buildUniqueSubDivisions,
} from "../../../utils/technicalReportLevel";
import TechnicalReportLevelFields from "./components/TechnicalReportLevelFields";
import usePerfEvalStickyHeaderHeight from "../../../hooks/usePerfEvalStickyHeaderHeight";
const TABLE_COLUMN_COUNT = 10;

const calcPercent = (part, whole) => {
  const numerator = Number(part) || 0;
  const denominator = Number(whole) || 0;
  if (!denominator) return 0;
  return (numerator / denominator) * 100;
};

const normalizeLineComplaintRow = (item) => {
  const registeredTotal = Number(
    item.totalRegistered ??
      item.Total ??
      item.total ??
      item.Registered ??
      item.registered ??
      item.ComplaintsRegistered ??
      item.complaintsRegistered ??
      0,
  );
  const resolvedTotal = Number(
    item.totalResolved ??
      item.Resolved ??
      item.resolved ??
      item.TotalResolved ??
      item.ComplaintsResolved ??
      item.complaintsResolved ??
      0,
  );
  const resolvedInTime = Number(
    item.totalResolvedInTime ??
      item.ResolvedInTime ??
      item.resolvedInTime ??
      item.InTime ??
      item.inTime ??
      0,
  );
  const resolvedLate = Number(
    item.totalResolvedLate ??
      item.ResolvedLate ??
      item.resolvedLate ??
      item.Late ??
      item.late ??
      0,
  );
  const inProgressTotal = Number(
    item.totalInProgress ??
      item.InProgress ??
      item.inProgress ??
      item.Pending ??
      item.pending ??
      0,
  );

  const officeNameRaw = String(item.officeName ?? item.OfficeName ?? "").trim();
  const officeCodeFromName = officeNameRaw.match(/^(\d+)/)?.[1] ?? "";

  return {
    subDivisionCode:
      item.OfficeCode ??
      item.officeCode ??
      (officeCodeFromName || "-"),
    officeName: officeNameRaw,
    registeredTotal,
    resolvedTotal,
    resolvedPercent: Number(
      item.ResolvedPercent ??
        item.resolvedPercent ??
        calcPercent(resolvedTotal, registeredTotal),
    ),
    resolvedInTime,
    inTimePercent: Number(
      item.ResolvedInTimePercent ??
        item.resolvedInTimePercent ??
        item.InTimePercent ??
        item.inTimePercent ??
        calcPercent(resolvedInTime, resolvedTotal),
    ),
    resolvedLate,
    latePercent: Number(
      item.ResolvedLatePercent ??
        item.resolvedLatePercent ??
        item.LatePercent ??
        item.latePercent ??
        calcPercent(resolvedLate, resolvedTotal),
    ),
    inProgressTotal,
    inProgressPercent: Number(
      item.InProgressPercent ??
        item.inProgressPercent ??
        calcPercent(inProgressTotal, registeredTotal),
    ),
  };
};

const buildLineComplaintTotals = (rows = []) =>
  rows.reduce(
    (acc, item) => ({
      registeredTotal: acc.registeredTotal + (item.registeredTotal || 0),
      resolvedTotal: acc.resolvedTotal + (item.resolvedTotal || 0),
      resolvedInTime: acc.resolvedInTime + (item.resolvedInTime || 0),
      resolvedLate: acc.resolvedLate + (item.resolvedLate || 0),
      inProgressTotal: acc.inProgressTotal + (item.inProgressTotal || 0),
    }),
    {
      registeredTotal: 0,
      resolvedTotal: 0,
      resolvedInTime: 0,
      resolvedLate: 0,
      inProgressTotal: 0,
    },
  );

const withLineComplaintTotalPercents = (totals) => ({
  ...totals,
  resolvedPercent: calcPercent(totals.resolvedTotal, totals.registeredTotal),
  inTimePercent: calcPercent(totals.resolvedInTime, totals.resolvedTotal),
  latePercent: calcPercent(totals.resolvedLate, totals.resolvedTotal),
  inProgressPercent: calcPercent(totals.inProgressTotal, totals.registeredTotal),
});

const resolveOfficeDisplayName = (officeCode, subDivisionsList = []) => {
  const codeStr = String(officeCode ?? "").trim();
  if (!codeStr || codeStr === "-") return "-";

  const name = getName(codeStr, subDivisionsList);
  const defaultLabels = new Set(["Company", "Circle", "Division", "Sub-Division"]);
  const resolvedName = name && !defaultLabels.has(name) ? name : "";

  if (codeStr.length === 2) {
    const company = companyShortName(codeStr);
    return company && company !== "DISTRIBUTION COMPANY NAME"
      ? `${company}-${codeStr}`
      : codeStr;
  }

  if (resolvedName) {
    return `${resolvedName}-${codeStr}`;
  }

  return codeStr;
};

const buildLineComplaintTableRows = (items, subDivisionsList = []) =>
  items
    .map((item) => {
      const row = normalizeLineComplaintRow(item);
      return {
        ...row,
        officeDisplayName:
          row.officeName ||
          resolveOfficeDisplayName(row.subDivisionCode, subDivisionsList),
      };
    })
    .sort((a, b) =>
      String(a.officeDisplayName).localeCompare(String(b.officeDisplayName)),
    );

const CADLineComplaintsStats = () => {

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Adding 1 because months are 0-indexed
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { token, code, name, roleName, roleTypeName, cscID, cfcLevelId, divisionCode, hierarchy: userHierarchy, selectedAdminCompanyCode } = useSelector(state => state.auth)
  const tableRef = useRef(null);
  const [assmntBatchWise, setAssmntBatchWise] = useState([]);
  // const { officeCode: offCode } = useSelector(state => state.officeCode)
  const offCode = userHierarchy == 1
    ? code
    : userHierarchy == 2
      ? code.slice(0, 2)
      : userHierarchy == 3
        ? code.slice(0, 3)
        : userHierarchy == 4
          ? code.slice(0, 4)
          : userHierarchy == 5 || userHierarchy == 52
            ? code.slice(0, 5)
            : code;
  const [tableData, setTableData] = useState([]);
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [circleCode, setCircleCode] = useState('');
  const [circleName, setCircleName] = useState('');
  const [officeCode, setOfficeCode] = useState();
  const [officeName, setOfficeName] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedSubDivision, setSelectedSubDivision] = useState('');
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [filteredSubDivisions, setFilteredSubDivisions] = useState([]);
  const [hierarchy, setHierarchy] = useState('');
  const [filteredCircles, setFilteredCircles] = useState([]);
const [isGoDisabled, setIsGoDisabled] = useState(true); 
  const [lastAppliedFilters, setLastAppliedFilters] = useState(null);
  const [appliedReportOfficeCode, setAppliedReportOfficeCode] = useState("");
  const [dateFrom, setDateFrom] = useState(getCurrentDate())
  const [dateTo, setDateTo] = useState(getCurrentDate())
  const [totals, setTotals] = useState({});
  const [complaints, setComplaints] = useState([])

  const [subDivisions, setSubDivisions] = useState(JSON.parse(window.localStorage.getItem('subDivisions')) || []);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState('');

  useEffect(() => {
    if (String(code) === "10" && selectedAdminCompanyCode) {
      const company = String(selectedAdminCompanyCode);
      setSelectedCompanyCode(company);
      setOfficeCode(company);
    }
  }, [code, selectedAdminCompanyCode]);

  const [currentBillingStats, setCurrentBillingStats] = useState({
    batches: [],
    amountsBatchWise: [],
    unitsBatchWise: [],
    categories: ['Total', 'Dom.', 'Com.', 'Ind.', 'Arg.', 'Oth.'],
    amountsCategoryWise: [],
  })
  const OffLevel = String(officeCode).length;
  const [billingReportPopup, setBillingReportPopup] = useState(false);

  const companyCode = code.slice(0, 2)

  const getLogo = (code) => {
    switch (code) {
      case '11':
        return LESCOLOGO
      case '12':
        return GEPCOLOGO
      case '13':
        return FESCOLOGO
      case '14':
        return IESCOLOGO
      case '15':
        return MEPCOLOGO
      case '16':
        return PESCOLOGO
      case '27':
        return SEPCOLOGO
      case '37':
        return HESCOLOGO
      case '48':
        return QESCOLOGO
      case '59':
        return TESCOLOGO
      default:
        return MEPCOLOGO
    }
  }

  const getCompanies = (code) => {
    switch (code) {
      case '11':
        return "Lahore Electric Supply Company"
      case '12':
        return "Gujranwala Electric Power Company"
      case '13':
        return "Faisalabad Electric Supply Company"
      case '14':
        return "Islamabad Electric Supply Company"
      case '15':
        return "Multan Electric Power Company"
      case '16':
        return "Peshawar Electric Supply Company"
      case '27':
        return "Sukkur Electric Power Company"
      case '37':
        return "Hyderabad Electric Supply Company"
      case '48':
        return "Quetta Electric Supply Company"
      case '59':
        return "Tribal Electric Supply Company"
      default:
        return "Multan Electric Power Company"
    }
  }

  const {
    reportLevel,
    appliedReportLevel,
    setAppliedReportLevel,
    availableReportLevelOptions,
    handleReportLevelChange,
    showCircleFilter,
    showDivisionFilter,
    showSubDivisionFilter,
    circleFilterEnabled,
    divisionFilterEnabled,
    subDivisionFilterEnabled,
    resolveApiOfficeCode,
  } = useTechnicalReportLevel({
    code,
    userHierarchy,
    cfcLevelId,
    hierarchy,
    offCode,
    subDivisions,
    selectedCompanyCode,
    selectedAdminCompanyCode,
    officeCode,
    circleCode,
    selectedDivision,
    selectedSubDivision,
    setCircleCode,
    setCircleName,
    setSelectedDivision,
    setSelectedSubDivision,
    setFilteredCircles,
    setFilteredDivisions,
    setFilteredSubDivisions,
    setOfficeCode,
    setOfficeName,
    getCompanies,
    getName,
  });

  const displayCount = (value, showZero = false) => {
    const count = Number(value) || 0;
    if (!showZero && count === 0) {
      return "-";
    }
    return formatWithCommas(count);
  };

  const displayPercent = (value, showZero = false) => {
    const percent = Number(value) || 0;
    if (!showZero && percent === 0) {
      return "-";
    }
    return percent.toFixed(2);
  };

  const reportTitlePrefix = "CAD Line Complaints Stats";

  const reportCompanyCode =
    selectedCompanyCode || selectedAdminCompanyCode || code.slice(0, 2);
  const companyTotalLabel = `${companyShortName(String(reportCompanyCode))} Total`;

  const officeCodeColumnLabel = "Office";

  const DownloadPdf = async (print) => {
    return new Promise((resolve) => {
      let offName = officeName || "MEPCO Region";
      const pdfOfficeCodeLabel = "Office";
      const title =
        dateFrom && dateTo && convertDate(dateFrom) === convertDate(dateTo)
          ? `${reportTitlePrefix} as on ${convertDate(dateTo)}`
          : `${reportTitlePrefix} from ${dateFrom ? convertDate(dateFrom) : "-"} to ${dateTo ? convertDate(dateTo) : "-"}`;
      const headers = [
        [
          { content: pdfOfficeCodeLabel, rowSpan: 2, styles: { halign: "left", valign: "middle" } },
          { content: "Complaints Registered", colSpan: 1, styles: { halign: "center" } },
          { content: "Complaints Resolved", colSpan: 2, styles: { halign: "center" } },
          { content: "Resolved Incl. Hold Time", colSpan: 4, styles: { halign: "center" } },
          { content: "In Progress", colSpan: 2, styles: { halign: "center" } },
        ],
        [
          "Total",
          "Total",
          "%",
          "InTime",
          "%",
          "Late",
          "%",
          "Total",
          "%",
        ].map((header) => ({
          content: header,
          styles: { halign: "center", fontStyle: "bold" },
        })),
      ];

      const body = [];
      const totals = withLineComplaintTotalPercents(buildLineComplaintTotals(tableData));

      if (tableData && tableData.length > 0) {
        tableData.forEach((item) => {
          body.push([
            { content: item.officeDisplayName || item.subDivisionCode || "-", styles: { halign: "left" } },
            { content: displayCount(item.registeredTotal, true), styles: { halign: "center" } },
            { content: displayCount(item.resolvedTotal, true), styles: { halign: "center" } },
            { content: displayPercent(item.resolvedPercent, true), styles: { halign: "center" } },
            {
              content: displayCount(item.resolvedInTime),
              styles: { halign: "center", textColor: [22, 163, 74] },
            },
            {
              content: displayPercent(item.inTimePercent),
              styles: { halign: "center", textColor: [22, 163, 74] },
            },
            {
              content: displayCount(item.resolvedLate),
              styles: { halign: "center", textColor: [220, 38, 38] },
            },
            {
              content: displayPercent(item.latePercent),
              styles: { halign: "center", textColor: [220, 38, 38] },
            },
            { content: displayCount(item.inProgressTotal, true), styles: { halign: "center" } },
            { content: displayPercent(item.inProgressPercent, true), styles: { halign: "center" } },
          ]);
        });

        body.push([
          {
            content: companyTotalLabel,
            styles: { halign: "left", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayCount(totals.registeredTotal, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayCount(totals.resolvedTotal, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayPercent(totals.resolvedPercent, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayCount(totals.resolvedInTime, true),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: "#f8f9fa",
              textColor: [22, 163, 74],
            },
          },
          {
            content: displayPercent(totals.inTimePercent, true),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: "#f8f9fa",
              textColor: [22, 163, 74],
            },
          },
          {
            content: displayCount(totals.resolvedLate, true),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: "#f8f9fa",
              textColor: [220, 38, 38],
            },
          },
          {
            content: displayPercent(totals.latePercent, true),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: "#f8f9fa",
              textColor: [220, 38, 38],
            },
          },
          {
            content: displayCount(totals.inProgressTotal, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayPercent(totals.inProgressPercent, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
        ]);
      } else {
        body.push([
          {
            content: "No Data Found",
            colSpan: TABLE_COLUMN_COUNT,
            styles: { halign: "center", textColor: [255, 0, 0], fontStyle: "bold" },
          },
        ]);
      }


      const pdf = new jsPDF('landscape', 'mm', 'a4', { compressPdf: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const logoWidth = companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 25 : 30;
      const logoHeight = companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 20 : 10;
      pdf.addImage(getLogo(companyCode), 'PNG', 15, 5, logoWidth, logoHeight);

      const eOpsLogoWidth = 20;
      const eOpsLogoHeight = 8;
      const eOpsLogoXPosition = companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 43 : 17;
      pdf.addImage(eOpsLogo, 'PNG', eOpsLogoXPosition, companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 12 : 17, eOpsLogoWidth, eOpsLogoHeight);

      pdf.setFontSize(10);
      // pdf.text(convertDatewithTime(new Date()), pageWidth - 50, 12);
      // pdf.text("Name: " + name, pageWidth - 50, 17);
      // pdf.text("Designation: " + roleName, pageWidth - 50, 22);

      pdf.text(convertDatewithTime(new Date()), pageWidth - 65, 12);
      pdf.text("Name: " + name, pageWidth - 65, 17);
      pdf.text("Designation: " + roleName, pageWidth - 65, 22);

      pdf.setFontSize(12);
      pdf.text(getCompanies(companyCode), pageWidth / 2, 12, { align: 'center' });
      pdf.text(`${reportTitlePrefix} - ${companyShortName(String(reportCompanyCode))}`, pageWidth / 2, 18, { align: 'center' });
      pdf.setFontSize(10);
      pdf.text(title, pageWidth / 2, 25, { align: 'center' });

      pdf.autoTable({
        startY: 28,
        head: headers,
        body: body,
        styles: { cellPadding: 1, fontSize: 8, halign: "center", lineWidth: 0.25, lineColor: "white" },
        margin: { top: 2 },
        theme: "striped",
        headStyles: { valign: "middle", fillColor: "#2980ba" },
        columnStyles: { 0: { halign: "left" } },
      });

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 45, pageHeight - 10);
      }

      if (print) {
        openJsPdfInNewTab(pdf, `${title}.pdf`);
      } else {
        resolve(pdf);
      }
    });
  };

  const exportToExcel = () => {
    const title =
      dateFrom && dateTo && convertDate(dateFrom) === convertDate(dateTo)
        ? `${reportTitlePrefix} as on ${convertDate(dateTo)}`
        : `${reportTitlePrefix} from ${dateFrom ? convertDate(dateFrom) : "-"} to ${dateTo ? convertDate(dateTo) : "-"}`;
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: 'Sheet1' });
    const ws = wb.Sheets['Sheet1'];

    const headingText = title;
    XLSX.utils.sheet_add_aoa(ws, [[headingText]], { origin: "A1" });

    const range = XLSX.utils.decode_range(ws["!ref"]);
    const columnCount = range.e.c - range.s.c + 1;

    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columnCount - 1 } }];

    if (!ws["A1"]) ws["A1"] = {};
    ws["A1"].s = { alignment: { horizontal: "center", vertical: "center" }, font: { bold: true } };

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout]), `${title}.xlsx`);
  };

  const printPDF = async () => {
    let pdf = await DownloadPdf(false);
    const pdfUrl = pdf.output('bloburl');

    // Create an iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);

    // Create a cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.position = 'fixed';
    cancelButton.style.top = '20px';
    cancelButton.style.right = '20px';
    cancelButton.style.padding = '10px 20px';
    cancelButton.style.backgroundColor = '#ff4d4d';
    cancelButton.style.color = 'white';
    cancelButton.style.border = 'none';
    cancelButton.style.borderRadius = '5px';
    cancelButton.style.cursor = 'pointer';
    document.body.appendChild(cancelButton);

    // Print the PDF when the iframe loads
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };

    // Remove iframe and cancel button when cancel button is clicked
    cancelButton.addEventListener('click', () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      if (cancelButton.parentNode) {
        document.body.removeChild(cancelButton);
      }
    });
  };

  function getCurrentDateFormatted(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const options = { month: 'short' };
    const month = date.toLocaleString('en-US', options);
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }

  // Fetch subDivisions for code == 10
  useEffect(() => {
    if (code == 10 && selectedCompanyCode && selectedCompanyCode !== "10") {
      const fetchSubDivisions = async () => {
        setViewBackdrop(true);
        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);
        const formdata = new FormData();
        formdata.append('companyCode', selectedCompanyCode);
        const requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: formdata,
          redirect: 'follow',
        };
        try {
          const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
          if (response.ok) {
            const result = await response.json();
            if (result.status === '200') {
              setSubDivisions(result.message);
              setApiData(result.message);
              setFilteredCircles(buildUniqueCircles(result.message));
              setFilteredDivisions(buildUniqueDivisions(result.message));
              setFilteredSubDivisions(buildUniqueSubDivisions(result.message));
            } else {
              setSubDivisions([]);
              setFilteredCircles([]);
              setFilteredDivisions([]);
              setFilteredSubDivisions([]);
            }
          } else {
            throw new Error(`Request failed with status ${response.status}`);
          }
        } catch (error) {
          setSubDivisions([]);
          setFilteredCircles([]);
          setFilteredDivisions([]);
          setFilteredSubDivisions([]);
        } finally {
          setViewBackdrop(false);
        }
      };
      fetchSubDivisions();
    } else if (code != 10) {
      setFilteredCircles(buildUniqueCircles(subDivisions));
      setFilteredDivisions(buildUniqueDivisions(subDivisions));
      setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
    }
  }, [code, selectedCompanyCode, token, cfpBaseUrl]);

  const handleChangeCompany = async (e) => {
    const selectedCompany = e.target.value;
    setSelectedCompanyCode(selectedCompany);

    if (code == 10 && selectedCompany === "10") {
      setOfficeCode("10");
      setOfficeName("PITC");
      setCircleCode('');
      setSelectedDivision('');
      setSelectedSubDivision('');
      setCircleName('');
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
      setFilteredCircles([]);
      return;
    }

    setOfficeCode(selectedCompany);
    setOfficeName(getCompanies(selectedCompany) || '');

    if (code == 10 && selectedCompany) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompany);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            // Reset dropdowns
            setCircleCode('');
            setSelectedDivision('');
            setSelectedSubDivision('');
            setCircleName('');

            setFilteredCircles(buildUniqueCircles(result.message));
            setFilteredDivisions(buildUniqueDivisions(result.message));
            setFilteredSubDivisions(buildUniqueSubDivisions(result.message));
          } else {
            setSubDivisions([]);
            setFilteredCircles([]);
            setFilteredDivisions([]);
            setFilteredSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredCircles([]);
        setFilteredDivisions([]);
        setFilteredSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (!selectedCompany) {
      // Reset when no company is selected
      setOfficeName('PITC');
      setOfficeCode('10');
      setCircleCode('');
      setSelectedDivision('');
      setSelectedSubDivision('');
      setCircleName('');
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
      setFilteredCircles([]);
      setSubDivisions([]);
      setApiData([]);
    }
  };

  const handleCircleChange = async (event) => {
    const selectedCircleCode = event.target.value ? parseInt(event.target.value) : '';
    if (code == 10 && selectedCircleCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      formdata.append('circleCode', selectedCircleCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setCircleCode(selectedCircleCode);
            setOfficeCode(selectedCircleCode);
            const circle = filteredCircles.find(circle => circle.circleCode === selectedCircleCode);
            const circleName = circle?.circleName || '';
            setOfficeName(circleName.concat(' Circle'));
            setCircleName(circleName);
            setSelectedDivision('');
            setSelectedSubDivision('');
            setFilteredSubDivisions([]);

            // Populate divisions
            const uniqueDivisions = result.message
              .filter(item => item.circleCode === selectedCircleCode)
              .reduce((acc, item) => {
                if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                  acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
                }
                return acc;
              }, []);
            setFilteredDivisions(uniqueDivisions);
          } else {
            setSubDivisions([]);
            setFilteredDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (code == 10 && !selectedCircleCode && selectedCompanyCode) {
      // Handle "All Circle" selection for code == 10
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setCircleCode('');
            setOfficeCode(selectedCompanyCode);
            setOfficeName(getCompanies(selectedCompanyCode));
            setCircleName('');
            setSelectedDivision('');
            setSelectedSubDivision('');
            setFilteredSubDivisions([]);

            // Populate divisions
            const uniqueDivisions = result.message.reduce((acc, item) => {
              if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          } else {
            setSubDivisions([]);
            setFilteredDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedCircleCode) {
      setCircleCode(selectedCircleCode);
      setOfficeCode(selectedCircleCode);
      const circle = filteredCircles.find(circle => circle.circleCode === selectedCircleCode);
      const circleName = circle?.circleName || '';
      setOfficeName(circleName.concat(' Circle'));
      setCircleName(circleName);
      setSelectedDivision('');
      setSelectedSubDivision('');
      setFilteredSubDivisions([]);
      const uniqueDivisions = subDivisions
        .filter(item => item.circleCode === selectedCircleCode)
        .reduce((acc, item) => {
          if (!acc.some(division => division.divisionCode === item.divisionCode)) {
            acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    } else {
      // Handle "All Circle" for code != 10
      setCircleCode('');
      setSelectedDivision('');
      setSelectedSubDivision('');
      setFilteredSubDivisions([]);
      if (roleTypeName == 'Technical') {
        if (offCode) {
          setOfficeCode(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode));
          let officeName = getName(String(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode)), subDivisions);
          setOfficeName(officeName.concat(' Region'));
          setCircleName('');
          const circles = subDivisions.reduce((acc, curr) => {
            if (!acc.some(item => item.circleCode === curr.circleCode)) {
              acc.push({ circleCode: curr.circleCode, circleName: curr.circleName, companyCode: curr.companyCode });
            }
            return acc;
          }, []);
          setFilteredCircles(circles);
          const uniqueDivisions = subDivisions.reduce((acc, item) => {
            if (!acc.some(division => division.divisionCode === item.divisionCode)) {
              acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
            }
            return acc;
          }, []);
          setFilteredDivisions(uniqueDivisions);
        }
      } else if (roleTypeName == 'Commercial') {
        if (cfcLevelId == 2) {
          if (offCode) {
            setOfficeCode(offCode.toString().slice(0, 2));
            let offName = cscID;
            setOfficeName(offName);
            setHierarchy(cfcLevelId);
            const uniqueDivisions = subDivisions.reduce((acc, item) => {
              if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          }
        } else if (cfcLevelId == 3) {
          if (offCode) {
            setOfficeCode(offCode.toString().slice(0, 3));
            let offName = cscID;
            setOfficeName(offName);
            setHierarchy(cfcLevelId);
            const uniqueDivisions = subDivisions.reduce((acc, item) => {
              if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          }
        }
      }
    }
  };

  const handleDivisionChange = async (e) => {
    const selectedDivisionCode = parseInt(e.target.value);
    if (code == 10 && selectedDivisionCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      formdata.append('divisionCode', selectedDivisionCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setSelectedDivision(selectedDivisionCode);
            setOfficeCode(selectedDivisionCode);
            const division = filteredDivisions.find(division => division.divisionCode === selectedDivisionCode);
            const divisionName = division?.divisionName || '';
            setOfficeName(divisionName.concat(' Division'));
            setSelectedSubDivision('');
            const uniqueSubDivisions = result.message
              .filter(item => item.divisionCode == selectedDivisionCode)
              .reduce((acc, item) => {
                if (!acc.some(sub => sub.subDivisionCode === item.subDivisionCode)) {
                  acc.push({ subDivisionCode: item.subDivisionCode, subDivisionName: item.subDivisionName, companyCode: item.companyCode });
                }
                return acc;
              }, []);
            setFilteredSubDivisions(uniqueSubDivisions);
          } else {
            setSubDivisions([]);
            setFilteredSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedDivisionCode) {
      setSelectedDivision(selectedDivisionCode);
      setOfficeCode(selectedDivisionCode);
      const divisionName = filteredDivisions.find(division => division.divisionCode === selectedDivisionCode)?.divisionName || '';
      setOfficeName(divisionName.concat(' Division'));
      setSelectedSubDivision('');
      const uniqueSubDivisions = subDivisions
        .filter(item => item.divisionCode == selectedDivisionCode)
        .reduce((acc, item) => {
          if (!acc.some(sub => sub.subDivisionCode === item.subDivisionCode)) {
            acc.push({ subDivisionCode: item.subDivisionCode, subDivisionName: item.subDivisionName, companyCode: item.companyCode });
          }
          return acc;
        }, []);
      setFilteredSubDivisions(uniqueSubDivisions);
    } else {
      setSelectedDivision('');
      setOfficeCode(circleCode ? circleCode : offCode);
      let officeName = getName(circleCode ? circleCode.toString() : offCode.toString(), subDivisions);
      setOfficeName(officeName.concat(' Circle'));
      setSelectedSubDivision('');
      setFilteredSubDivisions([]);
      const uniqueDivisions = subDivisions
        .filter(item => item.circleCode == (circleCode ? circleCode : offCode))
        .reduce((acc, item) => {
          if (!acc.some(division => division.divisionCode === item.divisionCode)) {
            acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    }
  };

  const handleSubDivisionChange = async (e) => {
    const selectedSubDivisionCode = parseInt(e.target.value);
    if (code == 10 && selectedSubDivisionCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      formdata.append('subDivisionCode', selectedSubDivisionCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setSelectedSubDivision(selectedSubDivisionCode);
            setOfficeCode(selectedSubDivisionCode);
            const subDivision = filteredSubDivisions.find(sub => sub.subDivisionCode === selectedSubDivisionCode);
            const subDivisionName = subDivision?.subDivisionName || '';
            setOfficeName(subDivisionName.concat(' Sub Division'));
          } else {
            setSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedSubDivisionCode) {
      setSelectedSubDivision(selectedSubDivisionCode);
      setOfficeCode(selectedSubDivisionCode);
      const subDivisionName = filteredSubDivisions.find(sub => sub.subDivisionCode === selectedSubDivisionCode)?.subDivisionName || '';
      setOfficeName(subDivisionName.concat(' Sub Division'));
    } else {
      setSelectedSubDivision('');
      let officeName = getName(selectedDivision ? selectedDivision.toString() : offCode.toString(), subDivisions);
      setOfficeName(officeName.concat(' Division'));
      setOfficeCode(selectedDivision ? selectedDivision : offCode);
    }
  };

  useEffect(() => {
    if (roleTypeName == 'Technical') {
      if (userHierarchy == 52 || userHierarchy == 5) {
        if (code) {
          setOfficeCode(String(code).slice(0, 5));
          setOfficeName(getName(String(code), subDivisions));
          setHierarchy(5);
        }
      } else if (offCode) {
        setOfficeCode(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode));
        let offName = getName(String(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode)), subDivisions);
        setOfficeName(offName);
        setHierarchy(offCode.toString().length);
      }
    } else if (roleTypeName == 'Commercial') {
      if (userHierarchy == 52 || userHierarchy == 5) {
        if (code) {
          setOfficeCode(String(code).slice(0, 5));
          setOfficeName(cscID || getName(code, subDivisions));
          setHierarchy(5);
        }
      } else if (cfcLevelId == 1) {
        if (offCode) {
          if (offCode == 10) {
            const effectiveCompany =
              selectedCompanyCode || selectedAdminCompanyCode || offCode;
            setOfficeCode(effectiveCompany);
            setOfficeName(getCompanies(String(effectiveCompany)) || "PITC");
          } else {
            setOfficeCode(offCode);
            let offName = getName(offCode, subDivisions);
            setOfficeName(offName);
          }

          setHierarchy(offCode.length);
        }
      } else if (cfcLevelId == 2) {
        if (offCode) {
          setOfficeCode(offCode.toString().slice(0, 2));
          let offName = cscID;
          setOfficeName(offName);
          setHierarchy(cfcLevelId);
        }
      } else if (cfcLevelId == 3) {
        if (offCode) {
          setOfficeCode(offCode.toString().slice(0, 3));
          let offName = cscID;
          setOfficeName(offName);
          setHierarchy(cfcLevelId);
        }
      } else if (cfcLevelId == 4) {
                if (offCode) {
                    setOfficeCode(offCode.toString().slice(0, 4));
                    let offName = cscID;
                    setOfficeName(offName);
                    setHierarchy(cfcLevelId);
                }
            }else if (cfcLevelId == 5) {
                if (offCode) {
                    setOfficeCode(offCode.toString().slice(0, 5));
                    let offName = cscID;
                    setOfficeName(offName);
                    setHierarchy(cfcLevelId);
                }
            }
    }
  }, [offCode, cfcLevelId, roleTypeName, cscID, selectedAdminCompanyCode, selectedCompanyCode, userHierarchy, code, subDivisions]);

  useEffect(() => {
    if (hierarchy) {
      if (hierarchy == 1 || cfcLevelId === 1) {
        setFilteredCircles(buildUniqueCircles(subDivisions));
        setFilteredDivisions(buildUniqueDivisions(subDivisions));
        setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      } else if (hierarchy == 2 || cfcLevelId == 2) {
        setFilteredCircles(buildUniqueCircles(subDivisions));
        setFilteredDivisions(buildUniqueDivisions(subDivisions));
        setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      } else if (hierarchy == 3 || cfcLevelId == 3) {
        applyScopedHierarchyFilterLists({
          items: subDivisions,
          effectiveHierarchy: 3,
          authCode: offCode,
          setCircleCode,
          setSelectedDivision,
          setSelectedSubDivision,
          setFilteredCircles,
          setFilteredDivisions,
          setFilteredSubDivisions,
        });
      } else if (hierarchy == 4 || cfcLevelId == 4) {
        applyScopedHierarchyFilterLists({
          items: subDivisions,
          effectiveHierarchy: 4,
          authCode: offCode,
          setCircleCode,
          setSelectedDivision,
          setSelectedSubDivision,
          setFilteredCircles,
          setFilteredDivisions,
          setFilteredSubDivisions,
        });
      } else if (
        hierarchy == 5 ||
        cfcLevelId == 5 ||
        userHierarchy == 52
      ) {
        applyScopedHierarchyFilterLists({
          items: subDivisions,
          effectiveHierarchy: 5,
          authCode: offCode,
          setCircleCode,
          setSelectedDivision,
          setSelectedSubDivision,
          setFilteredCircles,
          setFilteredDivisions,
          setFilteredSubDivisions,
        });
      }
    }
  }, [hierarchy, cfcLevelId, subDivisions, userHierarchy]);

  const handleGoClick = () => {
    const apiOfficeCode = resolveApiOfficeCode();
    const selectedReportLevel = Number(reportLevel);
    setAppliedReportOfficeCode(String(apiOfficeCode || ""));
    setAppliedReportLevel(reportLevel);
    fetchData(apiOfficeCode, dateFrom, dateTo, selectedReportLevel);

    setLastAppliedFilters({ ...currentFilters });

    setIsGoDisabled(true);
  };

  const fetchData = async (officeCode, dateFrom, dateTo, selectedReportLevel) => {
    setViewBackdrop(true);
    setTableData([]);

    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      officeCode: officeCode?.toString() || code.slice(0, 2),
      dateFrom,
      dateTo,
      reportLevel: selectedReportLevel,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        cfpBaseUrl + "lineComplaints/getPerformanceEvaluationReport",
        requestOptions,
      );
      const result = await response.json();

      if (result.status === "200" && Array.isArray(result.data)) {
        setTableData(buildLineComplaintTableRows(result.data, subDivisions));
      } else {
        setTableData([]);
      }
    } catch (error) {
      setTableData([]);
    }

    setViewBackdrop(false);
  };




  function formatWithCommas(value) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '5px',
    textAlign: 'center',
    fontSize: '15px',
  };


  const thTdStyle = {
    border: 'none',
    padding: '0px 8px',
    fontSize: '14px'
  };

  const subdivthTdStyle = {
    border: 'none',
    padding: '6px 7px',
    backgroundColor: '#d5e5fc',
    fontSize: '15px',
    fontWeight: 'bold'
  };

  const headerStyle = {
    backgroundColor: '#eeeff0',
    border: 'none',
    fontSize: '15px',
    paddingTop: '10px' // Extra space above headers
  };

  const currentFilters = {
      officeCode, dateFrom, dateTo, reportLevel
  };

  useEffect(() => {
    if (lastAppliedFilters === null) {
      setIsGoDisabled(false);
      return;
    }

    const hasChanged = JSON.stringify(currentFilters) !== JSON.stringify(lastAppliedFilters);
    setIsGoDisabled(!hasChanged);
  }, [
    officeCode, dateFrom, dateTo, reportLevel
  ]);

  const noDataAvailable = () => {
    alert("No data available to export or print.");
  };

  const reportOfficeLabel = officeName || "All Offices";
  const tableCaptionText =
    dateFrom && dateTo && convertDate(dateFrom) === convertDate(dateTo)
      ? `${reportTitlePrefix} - ${companyShortName(String(reportCompanyCode))} as on ${convertDate(dateTo)}`
      : `${reportTitlePrefix} - ${companyShortName(String(reportCompanyCode))} from ${dateFrom ? convertDate(dateFrom) : "-"} to ${dateTo ? convertDate(dateTo) : "-"}`;

  const tableTotals = withLineComplaintTotalPercents(buildLineComplaintTotals(tableData));

  usePerfEvalStickyHeaderHeight(tableRef, [tableData, viewBackdrop]);

  return (
    <>
      <Box className="form-box rpt-page coa-status-report slcs-page perf-eval-summary-page">
        <div className="coa-layout">
          <header className="coa-page-header">
            <h1 className="coa-page-title">CAD Line Complaints Stats</h1>
          </header>
          <div className="coa-toolbar-stack">
                <Paper
                  variant="outlined"
                  className="coa-card coa-card--filters"
                  sx={{
                    p: 2,
                    width: "100%",
                    bgcolor: "var(--eops-bg-form, #fff)",
                    borderRadius: 2,
                    borderColor: "divider",
                  }}
                >
                  <div
                    className="coa-filters coa-filters--mui-grid"
                    style={{ width: "100%" }}
                  >
                    <Grid container spacing={2} alignItems="flex-end">


                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Typography className="field-label">Date From</Typography>
                          <input
                            type="date"
                            className="date-field cs-filter-input"
                            value={dateFrom}
                            max={getTodayIsoDate()}
                            onChange={(e) => {
                              setDateFrom(clampIsoDateToToday(e.target.value));
                            }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Typography className="field-label">Date To</Typography>
                          <input
                            type="date"
                            className="date-field cs-filter-input"
                            value={dateTo}
                            max={getTodayIsoDate()}
                            onChange={(e) => {
                              setDateTo(clampIsoDateToToday(e.target.value));
                            }}
                          />
                        </Stack>
                      </Grid>
                      <TechnicalReportLevelFields
                        code={code}
                        reportLevel={reportLevel}
                        onReportLevelChange={handleReportLevelChange}
                        availableReportLevelOptions={availableReportLevelOptions}
                        selectedCompanyCode={selectedCompanyCode}
                        onChangeCompany={handleChangeCompany}
                        showCircleFilter={showCircleFilter}
                        circleCode={circleCode}
                        onChangeCircle={handleCircleChange}
                        filteredCircles={filteredCircles}
                        circleFilterEnabled={circleFilterEnabled}
                        showDivisionFilter={showDivisionFilter}
                        selectedDivision={selectedDivision}
                        onChangeDivision={handleDivisionChange}
                        filteredDivisions={filteredDivisions}
                        divisionFilterEnabled={divisionFilterEnabled}
                        showSubDivisionFilter={showSubDivisionFilter}
                        selectedSubDivision={selectedSubDivision}
                        onChangeSubDivision={handleSubDivisionChange}
                        filteredSubDivisions={filteredSubDivisions}
                        subDivisionFilterEnabled={subDivisionFilterEnabled}
                        companyPlaceholder="Select Company"
                      />
                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Box
                            aria-hidden
                            sx={{
                              height: "1.35rem",
                              mb: 0.5,
                              lineHeight: 1.35,
                            }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            className="coa-go-btn"
                            onClick={handleGoClick}
                            disabled={isGoDisabled || viewBackdrop}
                          >
                            GO
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </div>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    spacing={1}
                    className="coa-export-strip"
                    sx={{
                      pt: 2,
                      mt: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      gap: 1,
                    }}
                  >
                    <ReportExportButtons
                      isEmpty={tableData.length === 0}
                      onEmpty={noDataAvailable}
                      onPdf={() => DownloadPdf(true)}
                      onExcel={exportToExcel}
                      onPrint={printPDF}
                    />
                  </Stack>
                </Paper>
          </div>
          <div className="coa-report-banner perf-eval-banner" role="status">
            {tableCaptionText}
          </div>
          <Box
            className="application-table"
            sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
          >
            <div className="div-datagrid">
              <div className="reports-container">
                <div className="coa-table-panel">
                  <div className="coa-table-scroll" id="pdf-table">
                    <table className="perf-eval-matrix table" ref={tableRef}>
                      <thead>
                        <tr>
                          <th
                            rowSpan={2}
                            scope="col"
                            className="perf-eval-sticky-type"
                          >
                            {officeCodeColumnLabel}
                          </th>
                          <th colSpan={1} scope="colgroup">
                            Complaints Registered
                          </th>
                          <th colSpan={2} scope="colgroup">
                            Complaints Resolved
                          </th>
                          <th colSpan={4} scope="colgroup">
                            Resolved Incl. Hold Time
                          </th>
                          <th colSpan={2} scope="colgroup">
                            In Progress
                          </th>
                        </tr>
                        <tr>
                          <th scope="col">Total</th>
                          <th scope="col">Total</th>
                          <th scope="col">%</th>
                          <th scope="col">InTime</th>
                          <th scope="col">%</th>
                          <th scope="col">Late</th>
                          <th scope="col">%</th>
                          <th scope="col">Total</th>
                          <th scope="col">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData && tableData.length > 0 ? (
                          tableData.map((item, id) => (
                            <tr key={id}>
                              <td className="perf-eval-sticky-type">
                                {item.officeDisplayName || item.subDivisionCode || "-"}
                              </td>
                              <td>{displayCount(item.registeredTotal, true)}</td>
                              <td>{displayCount(item.resolvedTotal, true)}</td>
                              <td>{displayPercent(item.resolvedPercent, true)}</td>
                              <td className="slcs-resolved-in-time">
                                {displayCount(item.resolvedInTime)}
                              </td>
                              <td className="slcs-resolved-in-time">
                                {displayPercent(item.inTimePercent)}
                              </td>
                              <td className="slcs-resolved-late">
                                {displayCount(item.resolvedLate)}
                              </td>
                              <td className="slcs-resolved-late">
                                {displayPercent(item.latePercent)}
                              </td>
                              <td>{displayCount(item.inProgressTotal, true)}</td>
                              <td>{displayPercent(item.inProgressPercent, true)}</td>
                            </tr>
                          ))
                        ) : !viewBackdrop ? (
                          <tr className="coa-empty-row">
                            <td colSpan={TABLE_COLUMN_COUNT}>No Data Found</td>
                          </tr>
                        ) : null}
                      </tbody>
                      {tableData && tableData.length > 0 ? (
                        <tfoot>
                          <tr className="perf-eval-matrix__total-row">
                            <td className="perf-eval-sticky-type">{companyTotalLabel}</td>
                            <td>{displayCount(tableTotals.registeredTotal, true)}</td>
                            <td>{displayCount(tableTotals.resolvedTotal, true)}</td>
                            <td>{displayPercent(tableTotals.resolvedPercent, true)}</td>
                            <td className="slcs-resolved-in-time">
                              {displayCount(tableTotals.resolvedInTime, true)}
                            </td>
                            <td className="slcs-resolved-in-time">
                              {displayPercent(tableTotals.inTimePercent, true)}
                            </td>
                            <td className="slcs-resolved-late">
                              {displayCount(tableTotals.resolvedLate, true)}
                            </td>
                            <td className="slcs-resolved-late">
                              {displayPercent(tableTotals.latePercent, true)}
                            </td>
                            <td>{displayCount(tableTotals.inProgressTotal, true)}</td>
                            <td>{displayPercent(tableTotals.inProgressPercent, true)}</td>
                          </tr>
                        </tfoot>
                      ) : null}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </Box>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
            open={viewBackdrop}
          >
            <AppLoader />
          </Backdrop>
        </div>
      </Box>

    </>
  )
}

export default CADLineComplaintsStats;